import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComingSoonCta from '@/components/feature/ComingSoonCta';

const tracks = [
  { number: '01', title: 'Lluvia sobre el techo', description: 'lluvia suave y constante' },
  { number: '02', title: 'El río que canta', description: 'agua en movimiento, burbujeo suave' },
  { number: '03', title: 'Viento entre los árboles', description: 'viento grave entre hojas' },
  { number: '04', title: 'La selva al amanecer', description: 'sonidos de bosque y aves lejanas' },
  { number: '05', title: 'Olas en la orilla', description: 'mar calmo, ritmo constante' },
  { number: '06', title: 'La lluvia de la tarde', description: 'variación más intensa de la lluvia' },
  { number: '07', title: 'Arroyo entre piedras', description: 'agua con más burbujeo, menos flujo' },
  { number: '08', title: 'El mar de noche', description: 'olas más suaves y espaciadas' },
  { number: '09', title: 'Bosque con lluvia suave', description: 'combinación de bosque y lluvia' },
  { number: '10', title: 'El regreso al silencio', description: 'cierre con desvanecimiento gradual' },
];

const heroImage = 'https://readdy.ai/api/search-image?query=Digital%20art%20illustration%20in%20the%20style%20of%20premium%20editorial%20magazine%20cover%2C%20hyperrealistic%20digital%20render%20with%20soft%20painterly%20brush%20strokes%2C%20warm%20golden%20earth%20tones%2C%20soft%20textured%20lighting%2C%20a%20peaceful%20baby%20sleeping%20soundly%20in%20a%20soft%20crib%20surrounded%20by%20a%20gentle%20rain%20ambiance%2C%20soft%20curtains%20and%20warm%20cream%20and%20terracotta%20color%20palette%2C%20serene%20and%20natural%20atmosphere%2C%20artistic%20yet%20photorealistic%2C%20editorial%20premium%20quality%2C%20not%20a%20stock%20photo%2C%20not%20cold%20or%20clinical%2C%20warm%20neutral%20palette%2C%20natural%20elements%2C%20soothing%20and%20restful%20scene%2C%20soft%20textures%2C%20calming%20water%20elements%20subtly%20suggested&width=1400&height=600&seq=272&orientation=landscape';

export default function AlbumOndasDeCalma() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />

      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 z-50">
        <div
          className="h-full bg-terracotta transition-all duration-150"
          style={{ width: '0%' }}
          id="reading-progress"
        />
      </div>

      {/* Hero Section */}
      <section className="relative pt-28 md:pt-36 pb-12 md:pb-20 px-6 md:px-10">
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8 md:gap-16 items-start mb-10 md:mb-16">
            <div className="w-full md:w-1/2">
              <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
                {t('album_ondas.tag')}
              </span>
              <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold text-[#8B6F5C] leading-tight">
                {t('album_ondas.title1')}
                <br />
                <span className="font-bold">{t('album_ondas.title2')}</span>
              </h1>
            </div>
            <div className="w-full md:w-1/2 flex flex-col justify-end md:pt-12">
              <p className="font-sans text-base md:text-lg text-[#3D2B1F] leading-relaxed mb-6">
                {t('album_ondas.hero.desc')}
              </p>
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-[#5C4A3A] bg-white border border-sand/40 px-3 py-1.5 rounded-full">
                  <i className="ri-sun-cloudy-line text-xs" />
                  {t('album_ondas.badge1')}
                </span>
                <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-[#5C4A3A] bg-white border border-sand/40 px-3 py-1.5 rounded-full">
                  <i className="ri-user-smile-line text-xs" />
                  {t('album_ondas.badge2')}
                </span>
                <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-[#5C4A3A] bg-white border border-sand/40 px-3 py-1.5 rounded-full">
                  <i className="ri-music-line text-xs" />
                  {t('album.temas_badge')}
                </span>
                <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-[#5C4A3A] bg-white border border-sand/40 px-3 py-1.5 rounded-full">
                  <i className="ri-download-cloud-line text-xs" />
                  {t('album.descarga_badge')}
                </span>
                <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-[#5C4A3A] bg-white border border-sand/40 px-3 py-1.5 rounded-full">
                  <i className="ri-infinity-line text-xs" />
                  {t('album.uso_badge')}
                </span>
              </div>
              <div className="flex items-baseline gap-3 mb-6">
                <span className="font-serif text-4xl md:text-5xl font-bold text-[#8B6F5C]">$9.99</span>
                <span className="font-sans text-sm text-[#5C4A3A]">{t('common.usd')} — {t('common.pago_unico')}</span>
              </div>
              <ComingSoonCta />
            </div>
          </div>
          <div className="w-full h-[320px] md:h-[420px] rounded-3xl overflow-hidden">
            <img
              src={heroImage}
              alt="Bebé durmiendo en ambiente natural y sereno"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Sinopsis */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-[#4A3F35]">
        <div className="w-full max-w-3xl mx-auto text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-8">
            <i className="ri-rainy-line text-3xl text-cream" />
          </div>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-cream leading-tight mb-8">
            {t('album_ondas.sinopsis.title')}
          </h2>
          <p className="font-sans text-base md:text-lg text-cream leading-relaxed mb-6">
            Tu bebé no necesita un ambiente perfecto. Necesita un ambiente predecible. Sonidos de la Naturaleza lleva los sonidos de la naturaleza —lluvia, río, viento, olas— y los procesa para que sean el fondo sonoro perfecto: sin ruidos abruptos, sin sorpresas, sin dependencia.
          </p>
          <p className="font-sans text-base md:text-lg text-cream leading-relaxed mb-6">
            El ruido blanco y el ruido rosa filtran los sonidos del entorno sin generar adicción. La lluvia suave, el viento entre las hojas, las olas del mar lejanas: sonidos que el cerebro humano ha asociado con seguridad durante miles de años.
          </p>
          <p className="font-sans text-base md:text-lg text-cream leading-relaxed">
            No es tecnicismo. Es instinto. Es el sonido que escuchaban tus bisabuelas mientras amamantaban. Es el sonido que tu bebé reconoce como parte del mundo, no como una máquina.
          </p>
        </div>
      </section>

      {/* Neurociencia */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-white">
        <div className="w-full max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
            <div className="w-full md:w-1/2">
              <div className="w-14 h-14 rounded-2xl bg-[#AAB7A3]/20 flex items-center justify-center mb-6">
                <i className="ri-brain-line text-2xl text-[#AAB7A3]" />
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#8B6F5C] leading-tight mb-6">
                {t('album_ondas.ciencia.title')}
              </h2>
              <p className="font-sans text-base text-[#3D2B1F] leading-relaxed mb-4">
                Los sonidos naturales procesados —lluvia, río, viento, olas— filtran los ruidos abruptos del entorno sin generar dependencia. El cerebro humano evolucionó escuchando estos sonidos durante millones de años. No hay tecnología más antigua que la naturaleza calmando a un bebé.
              </p>
              <p className="font-sans text-base text-[#3D2B1F] leading-relaxed mb-4">
                El ruido blanco es esencialmente el sonido de todas las frecuencias audibles mezcladas en igual proporción. Para el cerebro de un bebé, que está constantemente procesando estímulos nuevos, el ruido blanco crea una especie de <em>manta acústica</em> que amortigua los sonidos sorpresa.
              </p>
              <p className="font-sans text-base text-[#3D2B1F] leading-relaxed mb-4">
                El ruido rosa, similar pero con más energía en las frecuencias bajas, es incluso más efectivo para el sueño. Su espectro de frecuencias se asemeja más a los sonidos naturales que el cerebro humano encuentra relajantes: el viento, la lluvia, las olas.
              </p>
              <p className="font-sans text-base text-[#3D2B1F] leading-relaxed">
                Los sonidos naturales procesados acompañan el descanso sin forzarlo. No es casualidad. Es evolución. Es neurociencia. Es la naturaleza hablando en el lenguaje que el cerebro entiende.
              </p>
            </div>
            <div className="w-full md:w-1/2">
              <div className="bg-cream rounded-3xl p-8 md:p-10 border border-sand/30">
                <h3 className="font-serif text-2xl font-bold text-[#8B6F5C] mb-6">
                  {t('album.que_incluye')}
                </h3>
                <ul className="flex flex-col gap-4">
                  {[
                    '10 temas de audio de alta calidad',
                    'Ruido blanco puro para siestas profundas',
                    'Ruido rosa con espectro natural relajante',
                    'Lluvia suave sin truenos ni sorpresas',
                    'Olas del mar en calma total',
                    'Viento entre hojas sin ráfagas abruptas',
                    'Filtrado de ruidos del entorno sin dependencia',
                    'Reducción de cortisol y activación del nervio vago',
                    'Descarga inmediata tras la compra',
                    'Uso ilimitado en todos tus dispositivos',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#AAB7A3]/20 flex-shrink-0">
                        <i className="ri-check-line text-[#AAB7A3] text-sm" />
                      </div>
                      <span className="font-sans text-sm text-[#3D2B1F]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Track List */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-3xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
              {t('album.lista_temas')}
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
              {t('album_ondas.tracks_title')}
            </h2>
            <p className="font-sans text-base text-[#3D2B1F] max-w-2xl mx-auto">
              Cada tema es un paisaje sonoro procesado para filtrar el caos del entorno y dejar solo lo esencial: calma, seguridad, previsibilidad.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-sand/40 overflow-hidden">
            {tracks.map((track, index) => (
              <div
                key={track.number}
                className={`flex items-center gap-4 md:gap-6 p-4 md:p-5 ${index !== tracks.length - 1 ? 'border-b border-sand/20' : ''}`}
              >
                <span className="font-sans text-sm font-medium text-[#5C4A3A] flex-shrink-0 w-8">
                  {track.number}
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-base md:text-lg font-bold text-[#8B6F5C]">
                    {track.title}
                  </h3>
                  <p className="font-sans text-sm text-[#5C4A3A] mt-0.5">
                    {track.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cierre emocional */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-[#4A3F35]">
        <div className="w-full max-w-3xl mx-auto text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-8">
            <i className="ri-rainy-line text-3xl text-cream" />
          </div>
          <p className="font-serif text-2xl md:text-3xl text-cream leading-relaxed mb-8">
            &ldquo;Antes de que existieran las casas, ya existía la lluvia sobre las hojas, el viento moviendo las ramas, el mar yendo y viniendo sin apuro. Esos sonidos son mucho más viejos que cualquier preocupación — y el cuerpo de tu bebé todavía los reconoce como parte del mundo seguro. No hay nada artificial en esta calma. Es la misma que sintieron todas las generaciones antes que vos, meciendo a sus hijos bajo el mismo cielo. Dejá que la naturaleza haga lo que siempre supo hacer: sostener sin esfuerzo.&rdquo;
          </p>
          <div className="w-16 h-px bg-cream/30 mx-auto mb-8" />
          <p className="font-sans text-base text-cream/80 leading-relaxed">
            — Mercedes, NeuroBabys
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
            {t('album_ondas.cta.title')}
          </h2>
          <p className="font-sans text-base text-[#3D2B1F] max-w-xl mx-auto mb-8">
            {t('album_ondas.cta.desc')}
          </p>
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className="font-serif text-5xl md:text-6xl font-bold text-[#8B6F5C]">$9.99</span>
            <span className="font-sans text-base text-[#5C4A3A]">USD</span>
          </div>
          <ComingSoonCta size="lg" className="mb-4" />
          <p className="font-sans text-sm text-[#5C4A3A]">
            {t('album.descarga_footer')}
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}